import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowUpRight,
  CheckCircle2,
  Copy,
  Lightbulb,
  Mail,
  MessageCircleQuestion,
  MessageSquare,
  Sparkles,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/** Single source of truth for the support email. Replace here when the
 *  support address changes (mirrors the "minimal for now" intent in the
 *  spec — no env-var or backend lookup yet). */
export const SUPPORT_EMAIL = 'info@infratailors.ai';

type HelpDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Modal users hit from either the topbar Help button or the sidebar
 * profile menu's "Get help" item. Both surfaces import this component —
 * there is no second copy of the contact UI.
 *
 * Design intent: minimal but premium. A small "halo" hero with our
 * support email front and centre, a one-tap copy button, a mailto CTA
 * that pre-fills a friendly subject, plus three tone-coloured cards that
 * tell users what kind of message helps us route fastest.
 */
export function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      toast.success(t('help.copied', { defaultValue: 'Email copied' }));
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('help.copyFailed', { defaultValue: 'Could not copy' }));
    }
  };

  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    t('help.mailSubject', { defaultValue: 'Help with Infratailors' }),
  )}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-0 sm:max-w-[460px]">
        {/* Halo header */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-primary/15 via-primary/5 to-transparent"
        />
        <DialogHeader className="relative flex flex-col items-center gap-3 px-6 pt-8 text-center">
          <div className="relative flex h-14 w-14 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary shadow-[0_0_0_6px_hsl(var(--primary)/0.08)]">
            <MessageCircleQuestion className="h-7 w-7" />
            <Sparkles className="absolute -right-1 -top-1 h-3.5 w-3.5 text-primary/80" />
          </div>
          <DialogTitle className="text-xl font-semibold">
            {t('help.title', { defaultValue: 'How can we help?' })}
          </DialogTitle>
          <DialogDescription className="max-w-sm text-sm text-muted-foreground">
            {t('help.subtitle', {
              defaultValue:
                "We're a small team. Drop us an email and a human will get back to you, usually within a business day.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="relative space-y-5 px-6 pb-6 pt-2">
          {/* Email card */}
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/50 text-foreground">
              <Mail className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('help.supportEmail', { defaultValue: 'Support email' })}
              </div>
              <a
                href={mailto}
                className="block truncate font-mono text-sm font-medium text-foreground hover:text-primary"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleCopy}
              aria-label={t('help.copy', { defaultValue: 'Copy email' })}
              className={cn(
                'h-9 w-9 shrink-0 transition-colors',
                copied && 'text-success',
              )}
            >
              {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>

          {/* Topics */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <TopicChip
              Icon={Lightbulb}
              tone="warning"
              title={t('help.topics.featureTitle', { defaultValue: 'Feature idea' })}
              hint={t('help.topics.featureHint', {
                defaultValue: 'Something to build',
              })}
            />
            <TopicChip
              Icon={MessageSquare}
              tone="info"
              title={t('help.topics.questionTitle', { defaultValue: 'Question' })}
              hint={t('help.topics.questionHint', {
                defaultValue: 'How does X work?',
              })}
            />
            <TopicChip
              Icon={MessageCircleQuestion}
              tone="destructive"
              title={t('help.topics.bugTitle', { defaultValue: 'Bug report' })}
              hint={t('help.topics.bugHint', {
                defaultValue: 'Something broken',
              })}
            />
          </div>

          {/* Primary CTA */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <DialogClose asChild>
              <Button variant="ghost">
                {t('actions.close', { defaultValue: 'Close' })}
              </Button>
            </DialogClose>
            <Button asChild>
              <a href={mailto}>
                <Mail className="mr-2 h-4 w-4" />
                {t('help.compose', { defaultValue: 'Compose email' })}
                <ArrowUpRight className="ml-1.5 h-3.5 w-3.5 opacity-80" />
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type Tone = 'info' | 'warning' | 'destructive';

const TONE_CLASSES: Record<Tone, string> = {
  info: 'border-info/30 bg-info/5 text-info',
  warning: 'border-warning/30 bg-warning/5 text-warning',
  destructive: 'border-destructive/30 bg-destructive/5 text-destructive',
};

function TopicChip({
  Icon,
  title,
  hint,
  tone,
}: {
  Icon: React.ElementType;
  title: string;
  hint: string;
  tone: Tone;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-lg border p-2.5',
        TONE_CLASSES[tone],
      )}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider">
        <Icon className="h-3 w-3" />
        {title}
      </div>
      <div className="text-[11px] leading-snug text-muted-foreground">{hint}</div>
    </div>
  );
}
